/**
 * Onboarding Modal Context
 *
 * Manages the multi-step modal onboarding flow
 * Coordinates between SignUpModal, NameStepModal, PreferenceStepModal,
 * BrandsStepModal, and CompletionModal
 *
 * Steps:
 * 1. Sign Up (email/password or OAuth)
 * 2. Name
 * 3. Preference (Men's/Women's/Both)
 * 4. Brands
 * 5. Completion (confirmation)
 */
"use client";

import { createContext, type ReactNode, useContext, useState } from "react";

import { useRouter } from "next/navigation";

import { BrandsStepModal } from "@/components/shared/BrandsStepModal";
import { CompletionModal } from "@/components/shared/CompletionModal";
import { NameStepModal } from "@/components/shared/NameStepModal";
import { PreferenceStepModal } from "@/components/shared/PreferenceStepModal";
import { SignUpModal } from "@/components/shared/SignUpModal";
import { useAuth } from "@/contexts/AuthContext";
import { setCachedUserGender } from "@/hooks/useUserGender";
import { LANDING_PAGE_PATH } from "@/lib/landing-page";
import { consumePendingAuthRedirect, peekPendingAuthRedirect } from "@/lib/pending-auth-redirect";
import { createUser, getUser, updateUser, updateUserPreferences } from "@/lib/shop-agent-client";

type OnboardingStep = "signup" | "name" | "preference" | "brands" | "completion";
type ShoppingPreference = "WOMEN" | "MEN" | "BOTH";

interface OnboardingData {
  email: string;
  firstName: string;
  shoppingPreference: ShoppingPreference | null;
  favoriteBrands: string[];
}

interface OnboardingModalContextType {
  startOnboarding: (initialStep?: OnboardingStep) => void;
  hideOnboarding: () => void;
  isOpen: boolean;
  currentStep: OnboardingStep;
}

const OnboardingModalContext = createContext<OnboardingModalContextType | undefined>(undefined);

export function OnboardingModalProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const {
    register,
    loginWithGoogle,
    loginWithApple,
    setOnboardingComplete,
    refreshOnboardingStatus,
    user,
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("signup");
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    email: "",
    firstName: "",
    shoppingPreference: null,
    favoriteBrands: [],
  });

  // Helper to ensure user exists in database before updating
  const ensureUserExists = async () => {
    try {
      // Try to get user first
      await getUser();
    } catch {
      // User doesn't exist, create them
      await createUser({
        display_name: user?.displayName || null,
        email: user?.email || null,
      });
    }
  };

  const startOnboarding = (initialStep: OnboardingStep = "signup") => {
    setCurrentStep(initialStep);
    setIsOpen(true);
  };

  const hideOnboarding = () => {
    setIsOpen(false);
    // Reset after animation
    setTimeout(() => {
      setCurrentStep("signup");
      setOnboardingData({
        email: "",
        firstName: "",
        shoppingPreference: null,
        favoriteBrands: [],
      });
    }, 300);
  };

  // Step handlers
  const handleEmailRegister = async (email: string, password: string) => {
    await register(email, password, "");

    // Save only email to state (NOT password for security)
    setOnboardingData((prev) => ({ ...prev, email }));

    // Move to name step
    setCurrentStep("name");
  };

  const handleGoogleSignUp = async () => {
    try {
      const result = await loginWithGoogle();

      if (result.status === "cancelled") {
        return;
      }

      if (result.status === "success") {
        // After Google sign-in, get user data and move to name step
        // User might already have a display name from Google
        setCurrentStep("name");
      }
      // For redirect flow, the page navigates away before the flow resumes here.
    } catch (error) {
      throw error;
    }
  };

  const handleAppleSignUp = async () => {
    try {
      const result = await loginWithApple();

      if (result.status === "cancelled") {
        return;
      }

      if (result.status === "success") {
        setCurrentStep("name");
      }
    } catch (error) {
      throw error;
    }
  };

  const handleNameSubmit = async (firstName: string) => {
    setOnboardingData((prev) => ({ ...prev, firstName }));

    // Ensure user exists in database, then update with name
    try {
      await ensureUserExists();
      await updateUser({ display_name: firstName });
    } catch (error) {
      console.error("Failed to update user name:", error);
      // Continue anyway - user can update profile later
    }

    setCurrentStep("preference");
  };

  const handlePreferenceSubmit = async (preference: ShoppingPreference) => {
    setOnboardingData((prev) => ({ ...prev, shoppingPreference: preference }));

    // Update user gender preference
    try {
      await ensureUserExists();
      const genderValue =
        preference === "MEN" ? "mens" : preference === "WOMEN" ? "womens" : "unisex";
      await updateUser({ gender: genderValue });
      // Update the cache so the new gender is immediately reflected
      if (user) {
        setCachedUserGender(user.uid, genderValue);
      }
    } catch (error) {
      console.error("Failed to update preference:", error);
      // Continue anyway
    }

    setCurrentStep("brands");
  };

  const handleBrandsSubmit = async (brands: string[]) => {
    setOnboardingData((prev) => ({ ...prev, favoriteBrands: brands }));

    // Update user preferences with brands
    try {
      await ensureUserExists();

      // Add all brands to user preferences
      if (brands.length > 0) {
        await updateUserPreferences("brands", brands, "add");
      }

      // Mark onboarding as complete
      await setOnboardingComplete();

      // Move to completion screen
      setCurrentStep("completion");
    } catch (error) {
      console.error("Failed to update brands:", error);
      // Still show completion even if update fails
      setCurrentStep("completion");
    }
  };

  const handleContinueShopping = async () => {
    // Refresh onboarding status to update the UI
    try {
      await refreshOnboardingStatus();
    } catch (error) {
      console.error("Failed to refresh onboarding status:", error);
    }

    hideOnboarding();

    // Wait a bit for state to propagate through auth context
    await new Promise((resolve) => setTimeout(resolve, 300));

    router.push(consumePendingAuthRedirect() ?? LANDING_PAGE_PATH);

    // Force a soft refresh to ensure navbar updates with new auth state
    router.refresh();
  };

  const handleBackFromName = () => {
    setCurrentStep("signup");
  };

  const handleBackFromPreference = () => {
    setCurrentStep("name");
  };

  const handleBackFromBrands = () => {
    setCurrentStep("preference");
  };

  const handleSwitchToSignIn = () => {
    const nextAction = peekPendingAuthRedirect()?.startsWith("/profile") ? "profile" : "general";

    // Close onboarding modal and show sign-in modal
    hideOnboarding();
    // Wait for modal close animation to complete before showing sign-in modal.
    // We dispatch a global event instead of directly calling the sign-up prompt context
    // to avoid provider-order circular dependencies.
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("signUpPrompt:show", {
          detail: { action: nextAction },
        })
      );
    }, 200);
  };

  const getStepNumber = (step: OnboardingStep): number => {
    const stepMap: Record<OnboardingStep, number> = {
      signup: 1,
      name: 2,
      preference: 3,
      brands: 4,
      completion: 5,
    };
    return stepMap[step];
  };

  return (
    <OnboardingModalContext.Provider
      value={{ startOnboarding, hideOnboarding, isOpen, currentStep }}
    >
      {children}

      {/* Render only the current step's modal */}
      {currentStep === "signup" && (
        <SignUpModal
          isOpen={isOpen}
          onClose={hideOnboarding}
          onRegister={handleEmailRegister}
          onGoogleSignUp={handleGoogleSignUp}
          onAppleSignUp={handleAppleSignUp}
          onSwitchToSignIn={handleSwitchToSignIn}
          currentStep={getStepNumber("signup")}
          totalSteps={4}
        />
      )}

      {currentStep === "name" && (
        <NameStepModal
          isOpen={isOpen}
          onClose={hideOnboarding}
          onNext={handleNameSubmit}
          onBack={handleBackFromName}
          initialFirstName={onboardingData.firstName}
          currentStep={getStepNumber("name")}
          totalSteps={4}
        />
      )}

      {currentStep === "preference" && (
        <PreferenceStepModal
          isOpen={isOpen}
          onClose={hideOnboarding}
          onNext={handlePreferenceSubmit}
          onBack={handleBackFromPreference}
          initialPreference={onboardingData.shoppingPreference}
          currentStep={getStepNumber("preference")}
          totalSteps={4}
        />
      )}

      {currentStep === "brands" && (
        <BrandsStepModal
          isOpen={isOpen}
          onClose={hideOnboarding}
          onNext={handleBrandsSubmit}
          onBack={handleBackFromBrands}
          initialBrands={onboardingData.favoriteBrands}
          shoppingPreference={onboardingData.shoppingPreference}
          currentStep={getStepNumber("brands")}
          totalSteps={4}
        />
      )}

      {currentStep === "completion" && (
        <CompletionModal
          isOpen={isOpen}
          onClose={hideOnboarding}
          onContinueShopping={handleContinueShopping}
          userEmail={onboardingData.email || user?.email || ""}
        />
      )}
    </OnboardingModalContext.Provider>
  );
}

export function useOnboardingModal() {
  const context = useContext(OnboardingModalContext);
  if (!context) {
    throw new Error("useOnboardingModal must be used within OnboardingModalProvider");
  }
  return context;
}
